/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 *
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @version 1.20.0
 * @since 1.0.0
 */

import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import { IOptionItemProps } from '../../../../components/common/Select/Select';
import { ISetFilterPayload } from '../../../../components/Table/helpers/filterAndSort';
import {
    getPageAccountInfoUrl,
    isPageAccountInfoUrlExternal,
} from '../../../../pages/PageAccountInfo';
import {
    API_SUBSCRIBER_TABLE_FILTER_AND_SORT,
    API_SUBSCRIBER_TABLE_META,
} from '../../../../pages/PageSubscriberMobile';
import { openExternalRoute, openRoute } from '../../../../routes/constants/externalRoutes';
import { CLIENT_ID, RoutePath } from '../../../../routes/constants/routesConstants';
import { appHistoryPush } from '../../../app/appHistory';
import { ISuperSearchTranslation } from '../../../internationalization/types/corporate';
import { guardComponentType } from '../../../role/reducer/guardReducer';
import { tableMetaFilterSelector } from '../../../table/selectors';
import { useSetTableFilterAndSortMutation } from '../../../table/store';
import { ISuperSearch } from '../../superSearchTypes';
import { SuperSearchListContainerStyled } from '../styled/SuperSearchListStyled';
import SuperSearchItemAccounts from './SuperSearchItemAccounts';
import SuperSearchItemEmpty from './SuperSearchItemEmpty';
import SuperSearchItemError from './SuperSearchItemError';
import SuperSearchItemMobileLabels from './SuperSearchItemMobileLabels';
import SuperSearchItemMobiles from './SuperSearchItemMobiles';
import SuperSearchItemSearch from './SuperSearchItemSearch';

/**
 * @description Super search list props
 * @interface ISuperSearchList
 * @property {() => void} closeSearch - close super search
 * @property {boolean} emptySearch - empty search result
 * @property {boolean} isSearch - searching flag
 * @property {string} message - error message
 * @property {ISuperSearch} searchResult - search result
 * @property {string} searchValue - search value
 * @property {(payload: ISetFilterPayload) => void} setFilterAccounts - set Accounts filters
 * @property {() => void} startSearch - start search request
 * @property {ICommonTranslation} translation - current locale
 */
export interface ISuperSearchList {
    closeSearch: () => void;
    emptySearch: boolean;
    componentsFeature: guardComponentType;
    isSearch: boolean;
    message: string;
    searchResult: ISuperSearch;
    searchValue: string;
    startSearch: () => void;
    translation: ISuperSearchTranslation;
}

const SuperSearchList = (props: ISuperSearchList) => {
    const {
        componentsFeature,
        searchResult,
    } = props;

    const [setTableFilterAndSort] = useSetTableFilterAndSortMutation();
    const filterSelector = useMemo(() => tableMetaFilterSelector(API_SUBSCRIBER_TABLE_META), []);
    const selectedFilters = useSelector(filterSelector);

    const setMobileLabel = (payload: ISetFilterPayload) => {
        const values = (payload.value as IOptionItemProps[])?.map(i => i.value) ?? [];

        setTableFilterAndSort({
            url: API_SUBSCRIBER_TABLE_FILTER_AND_SORT,
            filter: { ...selectedFilters, [payload.field]: { include: values } },
        }).then(() => {
            props.closeSearch();
            appHistoryPush(RoutePath.subscriberMobile);
        });
    };
    const setAccount = (value: string) => {
        const url = getPageAccountInfoUrl(componentsFeature, value);
        isPageAccountInfoUrlExternal(componentsFeature) ? openExternalRoute(url) : openRoute(url);
    };
    const setMobile = (value: string) => {
        openRoute(RoutePath.subscriberId.replace(CLIENT_ID, value));
    };

    return (
        <SuperSearchListContainerStyled>
            {
                searchResult.accounts.length > 0 &&
                <SuperSearchItemAccounts
                    accounts={searchResult.accounts}
                    searchValue={props.searchValue}
                    selectItem={setAccount}
                    translation={props.translation}
                />
            }
            {
                searchResult.mobileLabels.length > 0 &&
                <SuperSearchItemMobileLabels
                    mobileLabels={searchResult.mobileLabels}
                    searchValue={props.searchValue}
                    selectItem={setMobileLabel}
                    translation={props.translation}
                />
            }
            {
                searchResult.mobiles.length > 0 &&
                <SuperSearchItemMobiles
                    mobiles={searchResult.mobiles}
                    searchValue={props.searchValue}
                    selectItem={setMobile}
                    translation={props.translation}
                />
            }
            {
                props.isSearch &&
                <SuperSearchItemSearch/>
            }
            {
                props.emptySearch &&
                <SuperSearchItemEmpty translation={props.translation}/>
            }
            {
                !!props.message &&
                <SuperSearchItemError
                    startSearch={props.startSearch}
                    translation={props.translation}
                />
            }
        </SuperSearchListContainerStyled>
    );
};

export default SuperSearchList;
